import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, X } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { supabase } from '../../lib/supabaseClient';
import Seo from '../../components/common/Seo';
import ResponsiveImage from '../../components/common/ResponsiveImage';

export default function Gallery() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [storedGallery, setStoredGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = ['All', ...Array.from(new Set(storedGallery.map((item) => item.category)))];

  const filteredItems = selectedFilter === 'All'
    ? storedGallery
    : storedGallery.filter(item => item.category === selectedFilter);

  useEffect(() => {
    if (!selectedImage) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    if (closeButtonRef.current) closeButtonRef.current.focus();

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('gallery_items').select('*').order('created_at', { ascending: false });
      if (!error && data?.length) {
        const mapped = data.map((item) => ({
          id: item.id,
          title: item.title,
          category: item.category,
          image: item.image_url,
          type: item.type,
          date: item.date_label || ''
        }));
        setStoredGallery(mapped);
      }
      if (!error && !data?.length) setStoredGallery([]);
      setLoading(false);
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-game-black text-white">
      <Seo
        title="Gallery"
        description="Photos and videos from GameMeetsWorld streams, creators, and community moments."
        path="/gallery"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Gallery', path: '/gallery' }
        ]}
      />
      <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      <main className="pt-32 pb-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto px-6 lg:px-12 mb-16"
        >
          <h1 className="text-6xl font-bold mb-6">
            <span className="text-accent-green">Gallery</span>
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl">
            Explore our tournaments, events, and behind-the-scenes moments
          </p>
        </motion.div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-12">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <motion.button
                key={cat}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedFilter(cat)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  selectedFilter === cat
                    ? 'bg-accent-green text-black shadow-lg shadow-accent-green/50'
                    : 'bg-game-grey text-white hover:bg-accent-green/20 border border-accent-green/30'
                }`}
              >
                {cat}
              </motion.button>
            ))}
          </div>
        </div>
        {!loading && storedGallery.length === 0 && (
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <p className="text-white/60">No gallery items yet.</p>
          </div>
        )}

        {/* Gallery Grid */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div key={`gallery-skeleton-${idx}`} className="text-left rounded-xl bg-game-grey border border-accent-green/20 p-3 animate-pulse">
                  <div className="rounded-lg h-64 bg-white/10" />
                  <div className="pt-3 space-y-2">
                    <div className="h-3 w-20 bg-white/10 rounded" />
                    <div className="h-5 w-3/4 bg-white/10 rounded" />
                    <div className="h-3 w-24 bg-white/10 rounded" />
                    <div className="h-8 w-24 bg-white/10 rounded mt-2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {filteredItems.map((item, idx) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                  className="text-left rounded-xl bg-game-grey border border-accent-green/20 p-3"
                >
                  <div className="overflow-hidden rounded-lg h-64 bg-black/20">
                    {item.type === 'video' ? (
                    <video
                      src={item.image}
                      controls
                      preload="metadata"
                      className="w-full h-full object-cover"
                      playsInline
                    />
                  ) : (
                      <ResponsiveImage
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        sizes="(min-width: 1024px) 22vw, (min-width: 768px) 45vw, 92vw"
                        loading="lazy"
                        decoding="async"
                      />
                  )}
                  </div>
                  <div className="pt-3">
                    <p className="text-accent-green text-sm font-semibold">{item.category}</p>
                    <h3 className="text-white font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-text-secondary text-xs">{item.date}</p>
                    <button
                      type="button"
                      onClick={() => setSelectedImage(item)}
                      className="mt-3 px-3 py-2 text-sm bg-white/10 rounded hover:bg-accent-green hover:text-black transition-colors inline-flex items-center gap-2"
                    >
                      {item.type === 'video' ? <Play className="w-4 h-4" /> : null}
                      View full
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>

      {/* Lightbox Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedImage.title} preview`}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            className="relative max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            ref={modalRef}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-accent-green rounded-full p-2 hover:bg-accent-green/80 transition-colors z-10"
              ref={closeButtonRef}
            >
              <X className="w-6 h-6 text-black" />
            </button>
            {selectedImage.type === 'video' ? (
              <video
                src={selectedImage.image}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full max-h-[70vh] bg-black object-contain"
              />
            ) : (
              <ResponsiveImage
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full max-h-[70vh] object-contain bg-black"
                sizes="92vw"
                decoding="async"
              />
            )}
            <div className="p-6 bg-[#0A0E11]">
              <p className="text-accent-green font-semibold mb-2">{selectedImage.category}</p>
              <h2 className="text-3xl font-bold text-white mb-2">{selectedImage.title}</h2>
              <p className="text-text-secondary">{selectedImage.date}</p>
            </div>
          </motion.div>
        </motion.div>
      )}

      <Footer />
    </div>
  );
}
